import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Award, Zap, Target, MapPin } from 'lucide-react';
import { FlagImg } from '../components/FlagImg';

const values = [
  { icon: Shield, key: 'reliability', descKey: 'reliabilityDesc' },
  { icon: Award, key: 'consistency', descKey: 'consistencyDesc' },
  { icon: Zap, key: 'flexibility', descKey: 'flexibilityDesc' },
  { icon: Target, key: 'longTermVision', descKey: 'longTermVisionDesc' },
];

const locations = [
  { city: 'Seville', country: 'Spain', code: 'ES', x: '25%', y: '78%' },
  { city: 'Liège', country: 'Belgium', code: 'BE', x: '42%', y: '42%' },
];

const markets = [
  { countryKey: 'spain', code: 'ES' },
  { countryKey: 'portugal', code: 'PT' },
  { countryKey: 'france', code: 'FR' },
  { countryKey: 'belgium', code: 'BE' },
  { countryKey: 'netherlands', code: 'NL' },
  { countryKey: 'germany', code: 'DE' },
];

export const About = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-slate-50" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-20 md:py-32 bg-slate-900 text-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-green-500 text-sm uppercase tracking-wider font-bold mb-4 block">
              {t('aboutUs')}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-6">
              {t('aboutTitle')}
            </h1>
            <p className="text-slate-300 text-lg md:text-xl leading-relaxed">
              {t('aboutDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b border-slate-200" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="p-6">
              <div className="font-heading text-5xl md:text-6xl font-bold text-slate-900 mb-2">2</div>
              <div className="text-slate-600 text-sm uppercase tracking-wider">{t('plants')}</div>
            </div>
            <div className="p-6">
              <div className="font-heading text-5xl md:text-6xl font-bold text-slate-900 mb-2">10</div>
              <div className="text-slate-600 text-sm uppercase tracking-wider">{t('productionLines')}</div>
            </div>
            <div className="p-6">
              <div className="font-heading text-5xl md:text-6xl font-bold text-slate-900 mb-2">400K</div>
              <div className="text-slate-600 text-sm uppercase tracking-wider">{t('annualCapacity')}</div>
            </div>
            <div className="p-6">
              <div className="font-heading text-5xl md:text-6xl font-bold text-slate-900 mb-2">6</div>
              <div className="text-slate-600 text-sm uppercase tracking-wider">{t('marketsServed')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Presence / Map */}
      <section className="py-20 md:py-32" data-testid="about-map">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase tracking-tight mb-4">
              {t('ourPresence')}
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              {t('strategicFacilities')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Realistic Europe Map */}
            <div className="relative aspect-[4/3] overflow-hidden rounded-lg" style={{ backgroundColor: '#dce5ed' }}>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/europe-map-v2.svg`}
                alt="Europe Map"
                className="w-full h-full"
                style={{ objectFit: 'fill' }}
              />
              {/* Seville Marker - Lat 37.39, Lon -5.99 */}
              <div className="absolute flex flex-col items-center" style={{ left: '26.6%', top: '81.1%', transform: 'translate(-50%, -50%)' }}>
                <div className="bg-slate-900/90 text-white px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap rounded mb-1">
                  Seville, ES
                </div>
                <div className="w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white shadow-md" />
              </div>
              {/* Liège Marker - Lat 50.63, Lon 5.57 */}
              <div className="absolute flex flex-col items-center" style={{ left: '40.8%', top: '47.8%', transform: 'translate(-50%, -50%)' }}>
                <div className="bg-slate-900/90 text-white px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap rounded mb-1">
                  Liège, BE
                </div>
                <div className="w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white shadow-md" />
              </div>
            </div>

            {/* Markets Served */}
            <div>
              <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight mb-6">
                {t('marketsServed')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {markets.map((market) => (
                  <div
                    key={market.code}
                    className="flex items-center space-x-3 p-4 bg-white border border-slate-200"
                    data-testid={`market-${market.code}`}
                  >
                    <span className="text-2xl"><FlagImg code={market.code} size={28} /></span>
                    <span className="font-medium text-slate-900">{t(market.countryKey)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 space-y-4">
                {locations.map((loc) => (
                  <div
                    key={loc.city}
                    className="flex items-start space-x-4 p-4 bg-slate-900 text-white"
                    data-testid={`location-${loc.city.toLowerCase()}`}
                  >
                    <MapPin className="h-6 w-6 text-green-500 flex-shrink-0" />
                    <div>
                      <div className="font-heading text-lg font-bold uppercase">
                        {loc.city}, {loc.country} <FlagImg code={loc.code} size={22} className="ml-1" />
                      </div>
                      <div className="text-slate-400 text-sm">{t('productionPlant')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase tracking-tight mb-4">
              {t('ourValues')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-8 bg-slate-50 border border-slate-200 card-lift"
                data-testid={`value-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 text-white mb-6">
                  <value.icon className="h-8 w-8" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-3">
                  {t(value.key)}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t(value.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="about-history">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mb-16 text-center">
            {t('ourHistory')}
          </h2>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-700 hidden md:block" />

            <div className="space-y-12 md:space-y-0">
              {[
                { year: '1998', titleKey: 'historyFounded', descKey: 'historyFoundedDesc' },
                { year: '2005', titleKey: 'historyExpansion', descKey: 'historyExpansionDesc' },
                { year: '2014', titleKey: 'historyBelgium', descKey: 'historyBelgiumDesc' },
                { year: '2020', titleKey: 'historySustainability', descKey: 'historySustainabilityDesc' },
                { year: '2025', titleKey: 'historyToday', descKey: 'historyTodayDesc' },
              ].map((item, index) => (
                <div
                  key={item.year}
                  className={`md:flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className="bg-slate-800 p-6 inline-block">
                      <div className="font-heading text-3xl font-bold text-green-500 mb-2">
                        {item.year}
                      </div>
                      <div className="font-heading text-xl font-bold uppercase tracking-tight mb-2">
                        {t(item.titleKey)}
                      </div>
                      <p className="text-slate-400">{t(item.descKey)}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 bg-green-600 rounded-full z-10 mx-auto md:mx-0">
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </div>
                  <div className="md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
